import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Mail, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPasswordPage = () => {
  const { language } = useLanguage();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, { email });
      setSuccess(true);
    } catch (err) {
      setError(language === 'pt' 
        ? 'Erro ao processar pedido. Tente novamente.' 
        : 'Error processing request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-green-900 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-2xl p-8 text-center">
          <div className="flex justify-center mb-6">
            <div className="bg-green-100 p-4 rounded-full">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
          </div>
          <h2 className="text-2xl font-bold mb-4">
            {language === 'pt' ? 'Email Enviado!' : 'Email Sent!'}
          </h2>
          <p className="text-gray-600 mb-6">
            {language === 'pt' 
              ? 'Se o email existir na nossa base de dados, receberá instruções para redefinir a sua password.'
              : 'If the email exists in our database, you will receive instructions to reset your password.'}
          </p>
          <p className="text-sm text-gray-500 mb-6">
            {language === 'pt' 
              ? 'Verifique também a pasta de spam.'
              : 'Please also check your spam folder.'}
          </p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-6 py-3 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600"
          >
            <ArrowLeft className="h-5 w-5" />
            {language === 'pt' ? 'Voltar ao Login' : 'Back to Login'}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-green-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-2xl p-8">
        {/* Logo */}
        <div className="flex items-center justify-center space-x-2 mb-8">
          <img
            src="/icon-192x192.png"
            alt="Marketplace TVDE"
            className="h-10 w-10 rounded-lg object-cover ring-1 ring-slate-200"
          />
          <span className="text-3xl font-bold text-slate-900">
            Marketplace <span className="text-emerald-500">TVDE</span>
          </span>
        </div>

        <h2 className="text-2xl font-bold text-center mb-2">
          {language === 'pt' ? 'Recuperar Password' : 'Reset Password'}
        </h2>
        <p className="text-center text-gray-600 mb-6">
          {language === 'pt' 
            ? 'Insira o seu email e enviaremos instruções para redefinir a sua password.'
            : 'Enter your email and we will send you instructions to reset your password.'}
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
            <AlertCircle className="h-5 w-5" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder={language === 'pt' ? 'seu@email.com' : 'your@email.com'}
                required
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading 
              ? (language === 'pt' ? 'A enviar...' : 'Sending...') 
              : (language === 'pt' ? 'Enviar Email' : 'Send Email')}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link to="/login" className="text-sm text-gray-500 hover:text-gray-700 flex items-center justify-center gap-2">
            <ArrowLeft className="h-4 w-4" />
            {language === 'pt' ? 'Voltar ao Login' : 'Back to Login'}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
